import { type FunctionComponent, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";

import type { superSplashHomeV2Query$key } from "scmp-app/queries/__generated__/superSplashHomeV2Query.graphql";

import { Container } from "./styles";
import { HomeV2SuperSplashLarge } from "./variants/super-splash-large";
import { HomeV2SuperSplashLive } from "./variants/super-splash-live";

type Props = {
  className?: string;
  reference: superSplashHomeV2Query$key;
};

export const HomeV2SuperSplash: FunctionComponent<Props> = ({ className, reference }) => {
  const data = useFragment(
    graphql`
      fragment superSplashHomeV2Query on Query
      @argumentDefinitions(edition: { type: "LiveWidgetEditionEnum!" }) {
        # Keep in sync with every other liveWidget selection in homePageQuery
        # (apps/app/pages/home/index.page.tsx): the page-level spread of this
        # fragment and the homeSuperSplashInlineQuery inline fragment. Any
        # change to filter/orderBy or sub-fields must be applied to all of
        # them — relay-compiler requires identical argument values.
        liveWidget(
          filter: { edition: $edition }
          orderBy: { direction: DESC, field: UPDATED_DATE }
        ) {
          label
          style
          ...superSplashLargeHomeV2LiveWidget
          ...superSplashLiveHomeV2LiveWidget
        }
      }
    `,
    reference,
  );

  const style = data?.liveWidget?.style;

  const liveWidget = data?.liveWidget;

  const superSplashWidget = useMemo(() => {
    if (!liveWidget) return null;
    switch (style) {
      case "large_1":
      case "large_2":
        return <HomeV2SuperSplashLarge reference={liveWidget} />;
      case "live":
        return <HomeV2SuperSplashLive reference={liveWidget} />;
    }
    return null;
  }, [liveWidget, style]);

  if (!superSplashWidget) return null;

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.Breaking,
      }}
    >
      <Container $hasLabel={!!liveWidget?.label} className={className}>
        {superSplashWidget}
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2SuperSplash.displayName = "HomeV2SuperSplash";
